import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService from '../../services/AmplifyApiService';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';
import { EntityType, ITag } from '../../shared/interfaces/ITag';

const amplifyApiService = new AmplifyApiService();

const BASE_PATH = 'tags';

export type UnassignTagProps = {
  tagId: number;
  entityId: number;
  entityType: EntityType;
};

export type AssignTagProps = {
  name: string;
  entityId: number;
  entityType: EntityType;
};

// Hook for getting tags based on logged In User Company
export const useListTags = (
  options: UseQueryOptions<ITag[], Error> = {},
): UseQueryResult<ITag[]> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
  };

  const query = useQuery<ITag[], Error>({
    queryKey: [BASE_PATH],
    queryFn: async (): Promise<ITag[]> => {
      const response = await amplifyApiService.get<ITag[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

/**
 * Hook to un assign tag from an entity
 * @param options - React Query mutation options for the unassign operation.
 * @returns The result of the useMutation hook.
 */
export const useUnassignTagFromEntity = (
  options: UseMutationOptions<boolean, Error, UnassignTagProps> = {},
): UseMutationResult<boolean, Error, UnassignTagProps> => {
  const mutation = useMutation<boolean, Error, UnassignTagProps>({
    mutationFn: async ({ tagId, ...body }: UnassignTagProps) => {
      const path = `${BASE_PATH}/${tagId}/unassign`;
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path,
        options: { body },
      };

      return await amplifyApiService.post<Omit<UnassignTagProps, 'tagId'>, boolean>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};

/**
 * Hook to assign tag for entity and it internally create tag for company if not exists in DB with the same name
 * @param  options
 * @param  Error
 * @param  AssignTagProps
 * @returns boolean
 */
export const useAssignTagToEntity = (
  options: UseMutationOptions<boolean, Error, AssignTagProps> = {},
): UseMutationResult<boolean, Error, AssignTagProps> => {
  const mutation = useMutation<boolean, Error, AssignTagProps>({
    mutationFn: async (body: AssignTagProps) => {
      const path = `${BASE_PATH}/assign`;
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path,
        options: { body },
      };

      return await amplifyApiService.post<AssignTagProps, boolean>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};
